// ─── PROPOSER un rattachement, dans les deux sens ─────────────────
// « Il faut pouvoir se souvenir du rattachement pour le proposer, et que
// l'utilisateur le confirme » (Cédric). Rien n'est jamais appliqué d'office —
// règle générale de l'app, la même que pour les virements internes.
//
// Ce module COMPLÈTE `rapprochementLignes.js` (libellé + montant) par la seule
// chose qu'il ne sait pas faire : la MÉMOIRE. Si « VIREMENT HITACHI » a honoré
// le Salaire en juin et juillet, il doit arriver en tête pour août — même si
// le montant a bougé, même si le libellé a changé de référence.
//
// ⚠️ Une occurrence n'est candidate que si elle est MANQUANTE ou PARTIELLE :
// `scoreOccurrence` l'impose, et c'est juste — rattacher a une echeance deja
// realisee compterait la depense deux fois.
//
// Deux sens, une seule mécanique :
//   • depuis une OCCURRENCE → quelles opérations pourraient l'honorer ?
//   • depuis une OPÉRATION  → quelles occurrences pourrait-elle honorer ?
//
// Aucun accès Firestore ni React : testable tel quel.
import { motifDeLibelle } from './releveBancaire.js';
import { scoreOccurrence } from './rapprochementLignes.js';
import { repartitionDepense, EPSILON } from './repartition.js';

// Bonus d'un motif déjà rattaché à cette ligne par le passé. Volontairement
// FORT : c'est le signal le plus fiable dont on dispose — l'utilisateur l'a
// confirmé de sa main, au moins une fois.
const PTS_DEJA_VU = 120;

// Ce que l'utilisateur a confirmé par le passé : { ligneId → Set(motifs) }.
// On lit les rattachements EXISTANTS, pas une heuristique : c'est de la
// mémoire, pas de la devinette.
export function motifsConnus(rattachements = [], operations = []) {
  const parOp = new Map(operations.map(o => [o?.id, o]));
  const m = new Map();
  for (const r of rattachements) {
    const op = parOp.get(r?.depenseId);
    const motif = motifDeLibelle(op?.libelleBanque || op?.libelle);
    if (!r?.ligneId || !motif) continue;
    if (!m.has(r.ligneId)) m.set(r.ligneId, new Set());
    m.get(r.ligneId).add(motif);
  }
  return m;
}

// Une opération peut-elle encore recevoir un rattachement ?
const disponible = (op, rattachements) =>
  !!op && !op.exclu && repartitionDepense(op, rattachements).reste > EPSILON;

// Le même sens : rattacher une dépense à une prévision de revenu n'a pas de sens.
const memeSens = (op, ligne) => (op?.sens === 'revenu') === (ligne?.sens === 'revenu');

// ── Depuis une OCCURRENCE : quelles opérations l'honoreraient ? ───
// `mois` borne la recherche au mois de l'occurrence par défaut : une échéance
// d'août se règle avec un mouvement d'août. `moisVoisins` élargit d'un mois de
// part et d'autre — un prélèvement du 2 septembre peut honorer août.
export function proposerOperations({
  occurrence, operations = [], rattachements = [], moisVoisins = 1,
}) {
  if (!occurrence?.ligne) return [];
  const connus = motifsConnus(rattachements, operations);
  const dejaVus = connus.get(occurrence.ligneId) || new Set();
  const [y, m] = String(occurrence.mois || '').split('-').map(Number);
  const dansFenetre = (op) => {
    if (!y || !m) return true;
    const d = String(op.dateBanque || op.datePaiement || op.date || '').slice(0, 7);
    const [oy, om] = d.split('-').map(Number);
    if (!oy || !om) return false;
    return Math.abs((oy - y) * 12 + (om - m)) <= moisVoisins;
  };

  return operations
    .filter(op => disponible(op, rattachements) && memeSens(op, occurrence.ligne) && dansFenetre(op))
    .map(op => {
      const motif = motifDeLibelle(op.libelleBanque || op.libelle);
      const dejaVu = dejaVus.has(motif);
      return {
        operation: op,
        dejaVu,
        // Le score de rapprochement (libellé + montant) PLUS la mémoire.
        score: scoreOccurrence(op, occurrence) + (dejaVu ? PTS_DEJA_VU : 0),
        reste: repartitionDepense(op, rattachements).reste,
      };
    })
    // Un score nul signifie « aucun rapport » : le proposer ferait du bruit,
    // sauf s'il a déjà été confirmé — la mémoire prime sur l'heuristique.
    .filter(c => c.score > 0 || c.dejaVu)
    .sort((a, b) => b.score - a.score);
}

// ── Depuis une OPÉRATION : quelles occurrences honorerait-elle ? ──
export function proposerOccurrences({ operation, occurrences = [], rattachements = [], operations = [] }) {
  if (!operation) return [];
  const connus = motifsConnus(rattachements, operations);
  const motif = motifDeLibelle(operation.libelleBanque || operation.libelle);
  const dejaLiees = new Set(
    rattachements.filter(r => r?.depenseId === operation.id).map(r => `${r.ligneId}|${r.mois}`),
  );

  return occurrences
    .filter(o => o?.ligne && memeSens(operation, o.ligne) && !dejaLiees.has(`${o.ligneId}|${o.mois}`))
    .map(o => {
      const dejaVu = (connus.get(o.ligneId) || new Set()).has(motif);
      return {
        occurrence: o,
        dejaVu,
        score: scoreOccurrence(operation, o) + (dejaVu ? PTS_DEJA_VU : 0),
        // Ce que cette occurrence attend encore : sert de montant par défaut.
        manque: Math.max(0, Math.abs(o.prevu || 0) - Math.abs(o.reel || 0)),
      };
    })
    .filter(c => c.score > 0 || c.dejaVu)
    .sort((a, b) => b.score - a.score);
}

// La proposition mise en avant : la meilleure, et seulement si elle se
// détache. Deux candidats à égalité, c'est un choix — pas une suggestion.
export function preselection(candidats = []) {
  if (!candidats.length) return null;
  const [premier, second] = candidats;
  if (second && second.score >= premier.score) return null;
  return premier;
}

// ── Élargir au-delà des propositions (Cédric, 13/08) ─────────────
// `proposerOperations` écarte volontairement beaucoup : hors fenêtre de ±1
// mois, sens opposé, score nul. C'est ce qui rend la liste courte et utile —
// mais une opération hors de ces bornes devenait INTROUVABLE, sans recours.
//
// Cette fonction fabrique les mêmes candidats à partir d'une liste
// d'opérations DÉJÀ filtrée par l'utilisateur (recherche, filtres, tri) : on
// ne juge plus de la pertinence, il l'a fait lui-même.
//
// `dejaVu` et `score` restent renseignés — la mémoire des rattachements passés
// reste une information utile même quand on cherche à la main. Mais ils ne
// filtrent RIEN ici : ce que l'utilisateur a demandé à voir, il le voit.
export function candidatsElargis({
  occurrence, operations = [], rattachements = [], toutes = [],
}) {
  const connus = motifsConnus(rattachements, toutes.length ? toutes : operations);
  const dejaVus = (occurrence && connus.get(occurrence.ligneId)) || new Set();
  return operations.map(op => {
    const dejaVu = dejaVus.has(motifDeLibelle(op.libelleBanque || op.libelle));
    return {
      operation: op,
      dejaVu,
      score: occurrence ? scoreOccurrence(op, occurrence) : 0,
      reste: repartitionDepense(op, rattachements).reste,
    };
  });
}
